import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import { SITE_URL, SITE_NAME } from "@/seo/jsonLd";

type JsonLd = Record<string, unknown>;

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  nofollow?: boolean;
  jsonLd?: JsonLd | JsonLd[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  nofollow = false,
  jsonLd,
  article,
}: SEOProps) => {
  const { locale } = useTranslation();
  const { pathname } = useLocation();

  // Compute absolute URL for canonical & hreflang. Strip trailing slash for
  // consistency (root stays as "").
  const cleanPath = pathname === "/" ? "" : pathname.replace(/\/+$/, "");
  const url = canonical ?? `${SITE_URL}${cleanPath}`;

  // Sync <html lang> with active locale.
  useEffect(() => {
    document.documentElement.lang = locale === "fr" ? "fr-CA" : "en-CA";
  }, [locale]);

  const ogLocale = locale === "fr" ? "fr_CA" : "en_CA";
  const ogLocaleAlt = locale === "fr" ? "en_CA" : "fr_CA";

  const robots = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
    "max-image-preview:large",
    "max-snippet:-1",
    "max-video-preview:-1",
  ].join(",");

  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      {/* Primary meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />

      {/* Canonical + hreflang. Single-URL bilingual SPA: both languages share the URL. */}
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en-CA" href={url} />
      <link rel="alternate" hrefLang="fr-CA" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article-specific OG */}
      {type === "article" && article?.publishedTime ? (
        <meta property="article:published_time" content={article.publishedTime} />
      ) : null}
      {type === "article" && article?.modifiedTime ? (
        <meta property="article:modified_time" content={article.modifiedTime} />
      ) : null}
      {type === "article" && article?.author ? (
        <meta property="article:author" content={article.author} />
      ) : null}
      {type === "article" && article?.section ? (
        <meta property="article:section" content={article.section} />
      ) : null}
      {type === "article" && article?.tags
        ? article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))
        : null}

      {/* JSON-LD */}
      {jsonLdArray.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
